import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { collectionShowcaseAction } from 'src/reducers/collection/action'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import useWorkspaces from 'src/hooks/useWorkspaces'
import { shopLandingPageGetService } from 'src/api/collection'
import { useWorkspaceLogoState } from 'src/utils/workspace-logo'
import {
  PortalBreadcrumbBar,
  PortalFooterBar,
  PortalHeaderBar,
} from 'src/pages/collection/portal-page/portal-bars'
import useSignInLabel from 'src/pages/collection/portal-page/useSignInLabel'

const useStyles = makeStyles({
  // The preview keeps the page's full width; the portal bars wrap it.
  stage: {
    display: 'flex',
  },
  frame: {
    width: '100%',
    overflow: 'hidden',
    background: '#FFFFFF',
  },
  // Room for the header while it loads, so the page below doesn't jump.
  headerPlaceholder: {
    borderBottom: '1px solid #F1F3F5',
  },
  note: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '12px 20px',
    borderTop: '1px solid #E5E7EB',
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: '12px',
    lineHeight: 1.5,
    color: '#637381',
  },
  noteIcon: {
    width: 14,
    height: 14,
    flex: 'none',
    marginTop: 2,
    color: '#015FFB',
  },
  link: {
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    font: 'inherit',
    color: '#015FFB',
  },
})

/** The workspace portal's settings (Portal › Navigation / Footer), or null. */
const usePortalSettings = enabled => {
  const [portal, setPortal] = useState(null)
  useEffect(() => {
    if (!enabled) return undefined
    let active = true
    shopLandingPageGetService()
      .then(response => active && setPortal(response?.data || null))
      .catch(() => {})
    return () => {
      active = false
    }
  }, [enabled])
  return portal
}

/**
 * Landing page preview stage. In portal context the page sits between the
 * portal's own header and footer, drawn as learners see them.
 */
const PortalFrame = ({
  inPortal,
  entityLabel,
  showNote = true,
  // Path and course pages: the trail after Home (path name, course name).
  breadcrumbs,
  children,
}) => {
  const classes = useStyles()
  const history = useHistory()
  const dispatch = useDispatch()
  const { currentWorkspace } = useWorkspaces()
  const shopName = currentWorkspace?.shopName || ''
  const portal = usePortalSettings(inPortal)
  const { logo, loaded: logoLoaded } = useWorkspaceLogoState()
  const [signInLabel] = useSignInLabel()
  // The header is drawn once its settings and the logo are both known, so it
  // never swaps the workspace name for the logo; a placeholder keeps its room.
  const showBars = inPortal && portal && logoLoaded
  // Home under the name its menu item was given.
  const homeLabel =
    (portal?.pages || []).find(page => page.type === 'home')?.name || 'Home'
  const crumbs = (breadcrumbs || []).filter(Boolean)

  return (
    <>
      <div className={classes.stage}>
        <div className={classes.frame}>
          {/* Portal › Navigation / Footer can turn either bar off. */}
          {showBars && portal.showHeader !== false && (
            <PortalHeaderBar
              portal={portal}
              logo={logo}
              shopName={shopName}
              signInLabel={signInLabel}
            />
          )}
          {/* Also with the header hidden: then it is the way back. */}
          {showBars &&
            portal.showBreadcrumbs !== false &&
            crumbs.length > 0 && (
              <PortalBreadcrumbBar
                portal={portal}
                items={[homeLabel, ...crumbs]}
              />
            )}
          {inPortal && !showBars && (
            <div
              className={classes.headerPlaceholder}
              // Header height: the logo (40px by default) plus 16px padding,
              // at least 64px.
              style={{
                height: Math.max(64, (portal?.headerLogoSize || 40) + 32),
              }}
            />
          )}
          {children}
          {showBars && portal.showFooter !== false && (
            <PortalFooterBar portal={portal} logo={logo} shopName={shopName} />
          )}
        </div>
      </div>
      {showNote && (
        <div className={classes.note}>
          <Icon icon="tabler:info-circle" className={classes.noteIcon} />
          <span>
            {inPortal
              ? 'Opened from the portal, this page sits between the portal header and footer.'
              : `Opened from a direct link, only the ${entityLabel} content is shown.`}{' '}
            Navigation and footer belong to the{' '}
            <button
              type="button"
              className={classes.link}
              onClick={() =>
                dispatch(
                  collectionShowcaseAction({}, response =>
                    history.push(`/collection/${response.id}/landing-page`),
                  ),
                )
              }
            >
              Portal
            </button>
            , not to this {entityLabel}. The domain comes from{' '}
            <button
              type="button"
              className={classes.link}
              onClick={() => history.push('/workspace/custom-domain')}
            >
              Custom domain
            </button>
            .
          </span>
        </div>
      )}
    </>
  )
}

export default PortalFrame
